/**
 * Analytics Page
 *
 * Compliance metrics and verification insights dashboard.
 * Migrated to Tailwind + shadcn/ui
 */

import React, { useState, useMemo } from 'react';
import {
  BarChart3,
  Download,
  Calendar,
  RefreshCw,
  Loader2,
  Scan,
} from 'lucide-react';
import {
  useAllAnalytics,
  useExportAnalytics,
} from '../../hooks';
import { useTestMode } from '../../contexts/TestModeContext';
import OverviewCards from './OverviewCards';
import TrendsChart from './TrendsChart';
import FunnelChart from './FunnelChart';
import GeoDistribution from './GeoDistribution';
import RiskHistogram from './RiskHistogram';
import SLAGauge from './SLAGauge';
import ScreeningAnalytics from './ScreeningAnalytics';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const DATE_PRESETS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Custom', value: 'custom' },
];

const GRANULARITY_OPTIONS = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
];

const ANALYTICS_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'screening', label: 'Screening', icon: Scan },
];

export default function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [datePreset, setDatePreset] = useState(30);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [granularity, setGranularity] = useState('day');
  const [showDateMenu, setShowDateMenu] = useState(false);

  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    if (datePreset === 'custom' && customDateRange.start && customDateRange.end) {
      return {
        startDate: new Date(customDateRange.start),
        endDate: new Date(customDateRange.end),
      };
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (typeof datePreset === 'number' ? datePreset : 30));
    return { startDate: start, endDate: end };
  }, [datePreset, customDateRange]);

  // Environment
  const { isTestMode } = useTestMode();

  // Fetch all analytics data in a single request (fast!)
  const {
    overview,
    funnel,
    trends,
    geography,
    risk,
    sla,
    isLoading,
    refetch,
  } = useAllAnalytics(startDate, endDate, granularity, { is_sandbox: isTestMode });

  const exportAnalytics = useExportAnalytics();

  const handleRefresh = () => {
    refetch();
  };

  const handleExport = () => {
    exportAnalytics.mutate({ startDate, endDate, format: 'csv', is_sandbox: isTestMode });
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateMenu(false);
  };

  const currentPresetLabel = DATE_PRESETS.find(
    (p) => p.value === datePreset
  )?.label || 'Last 30 days';

  return (
    <div className="max-w-[1400px]">
      {/* Page Header */}
      <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-teal-500" />
          <div>
            <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50 m-0">
              Analytics
            </h1>
            <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
              Compliance metrics and verification insights
            </p>
          </div>
        </div>

        <div className="flex gap-3 items-center flex-wrap">
          {/* Date Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-grey-500 dark:text-grey-400">Period:</span>
            <div className="relative">
              <button
                className="flex items-center gap-2 px-3.5 py-2 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 cursor-pointer transition-colors hover:bg-grey-50 dark:hover:bg-grey-800 hover:border-teal-500"
                onClick={() => setShowDateMenu(!showDateMenu)}
              >
                <Calendar size={16} className="text-grey-400" />
                {currentPresetLabel}
              </button>

              {showDateMenu && (
                <div className="absolute top-full right-0 mt-1 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700 rounded-lg p-2 min-w-[180px] z-[100] shadow-lg">
                  {DATE_PRESETS.map((preset) => (
                    <div
                      key={preset.value}
                      className={cn(
                        'px-3 py-2.5 rounded-md text-sm cursor-pointer transition-colors',
                        datePreset === preset.value
                          ? 'bg-teal-500/10 text-teal-500'
                          : 'text-grey-500 dark:text-grey-400 hover:bg-grey-100 dark:hover:bg-grey-800 hover:text-grey-900 dark:hover:text-grey-50'
                      )}
                      onClick={() => handleDatePresetChange(preset.value)}
                    >
                      {preset.label}
                    </div>
                  ))}

                  {datePreset === 'custom' && (
                    <div className="flex gap-2 px-3 py-2 border-t border-grey-200 dark:border-grey-700 mt-2">
                      <input
                        type="date"
                        className="flex-1 px-2 py-1.5 bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-md text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:border-teal-500"
                        value={customDateRange.start}
                        onChange={(e) =>
                          setCustomDateRange((prev) => ({ ...prev, start: e.target.value }))
                        }
                      />
                      <input
                        type="date"
                        className="flex-1 px-2 py-1.5 bg-grey-100 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-md text-sm text-grey-900 dark:text-grey-50 focus:outline-none focus:border-teal-500"
                        value={customDateRange.end}
                        onChange={(e) =>
                          setCustomDateRange((prev) => ({ ...prev, end: e.target.value }))
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Granularity Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-grey-500 dark:text-grey-400">Group by:</span>
            <select
              className="px-3 py-2 bg-white dark:bg-grey-900 border border-grey-200 dark:border-grey-700 rounded-lg text-sm text-grey-900 dark:text-grey-50 cursor-pointer focus:outline-none focus:border-teal-500"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}
            >
              {GRANULARITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </Button>

          <Button
            onClick={handleExport}
            disabled={exportAnalytics.isPending}
          >
            {exportAnalytics.isPending ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download size={16} />
                Export CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-5 border-b border-grey-200 dark:border-grey-700 pb-0">
        {ANALYTICS_TABS.map((tab) => (
          <button
            key={tab.id}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.id
                ? 'text-teal-500 border-teal-500'
                : 'text-grey-500 dark:text-grey-400 border-transparent hover:text-grey-900 dark:hover:text-grey-50'
            )}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`analytics-tab-${tab.id}`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          <OverviewCards data={overview} isLoading={isLoading} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <TrendsChart
              data={trends}
              isLoading={isLoading}
              granularity={granularity}
            />
            <FunnelChart data={funnel} isLoading={isLoading} />
            <GeoDistribution data={geography} isLoading={isLoading} />
            <RiskHistogram data={risk} isLoading={isLoading} />
            <SLAGauge data={sla} isLoading={isLoading} />
          </div>
        </>
      )}

      {activeTab === 'screening' && (
        <ScreeningAnalytics
          startDate={startDate}
          endDate={endDate}
          granularity={granularity}
        />
      )}
    </div>
  );
}
